import { useEffect, useState, useCallback } from 'react'
import useAppStore from '../../stores/appStore'
import { getChapter, chapters, poems } from '../../data/poems'
import { useMobile } from '../../hooks/useMobile'
import './Navigation.css'

/**
 * Mobile hamburger menu button
 */
function MobileMenuButton({ isOpen, onClick }) {
  return (
    <button
      className={`mobile-menu-btn ${isOpen ? 'open' : ''}`}
      onClick={onClick}
      aria-label={isOpen ? 'Close menu' : 'Open menu'}
      aria-expanded={isOpen}
    >
      <span className="hamburger-line" />
      <span className="hamburger-line" />
      <span className="hamburger-line" />
    </button>
  )
}

/**
 * Mobile status bar - always visible at top on mobile
 */
function MobileStatusBar({ chapter, currentChapter, currentPoem, onMenuClick, isMenuOpen }) {
  return (
    <div className="mobile-status-bar">
      <MobileMenuButton isOpen={isMenuOpen} onClick={onMenuClick} />
      <div className="mobile-status-info">
        {chapter ? (
          <>
            <span className="mobile-chapter-label">Ch. {currentChapter + 1}</span>
            <span className="mobile-divider">|</span>
            <span className="mobile-poem-label">Poem {currentPoem + 1}/{poems[currentChapter + 1]?.length || 9}</span>
          </>
        ) : (
          <span className="mobile-overview-label">Overview</span>
        )}
      </div>
      <div className="mobile-title">The Path(os)</div>
    </div>
  )
}

/**
 * Navigation component with mobile drawer support
 */
function Navigation() {
  const {
    currentChapter,
    currentPoem,
    showUI,
    showChapterList,
    mobileNavOpen,
    nextPoem,
    prevPoem,
    nextChapter,
    prevChapter,
    resetToOverview,
    toggleChapterList,
    setCurrentChapter,
    toggleMobileNav,
    closeMobileNav
  } = useAppStore()

  const { isMobileOrTablet, isTouch } = useMobile()
  const [isExpanded, setIsExpanded] = useState(true)

  // Select a chapter and close menus
  const handleChapterClick = useCallback((chapterIndex) => {
    setCurrentChapter(chapterIndex)
    if (isMobileOrTablet) {
      closeMobileNav()
    }
    // Close chapter list after selection
    if (showChapterList) {
      toggleChapterList()
    }
  }, [setCurrentChapter, isMobileOrTablet, closeMobileNav, toggleChapterList, showChapterList])

  // Mobile swipe gesture handling
  useEffect(() => {
    if (!isMobileOrTablet || !isTouch) return

    let touchStartY = 0
    let touchStartX = 0
    const SWIPE_THRESHOLD = 50

    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY
      touchStartX = e.touches[0].clientX
    }

    const handleTouchEnd = (e) => {
      const touchEndY = e.changedTouches[0].clientY
      const touchEndX = e.changedTouches[0].clientX
      const diffY = touchStartY - touchEndY
      const diffX = Math.abs(touchStartX - touchEndX)

      // Only handle vertical swipes (not horizontal)
      if (diffX < SWIPE_THRESHOLD) {
        if (diffY > SWIPE_THRESHOLD && !mobileNavOpen) {
          // Swipe up - open nav
          toggleMobileNav()
        } else if (diffY < -SWIPE_THRESHOLD && mobileNavOpen) {
          // Swipe down - close nav
          closeMobileNav()
        }
      }
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [isMobileOrTablet, isTouch, mobileNavOpen, toggleMobileNav, closeMobileNav])

  // Keyboard controls (desktop only)
  useEffect(() => {
    if (isMobileOrTablet) return

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          prevPoem()
          break
        case 'ArrowRight':
          e.preventDefault()
          nextPoem()
          break
        case 'ArrowUp':
          e.preventDefault()
          prevChapter()
          break
        case 'ArrowDown':
          e.preventDefault()
          nextChapter()
          break
        case 'Escape':
          e.preventDefault()
          resetToOverview()
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [nextPoem, prevPoem, nextChapter, prevChapter, resetToOverview, isMobileOrTablet])

  // Close mobile nav on escape
  useEffect(() => {
    if (!mobileNavOpen) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeMobileNav()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [mobileNavOpen, closeMobileNav])

  if (!showUI) return null

  const chapter = currentChapter !== null ? getChapter(currentChapter + 1) : null

  // Mobile layout
  if (isMobileOrTablet) {
    return (
      <>
        {/* Mobile Status Bar - Always visible */}
        <MobileStatusBar
          chapter={chapter}
          currentChapter={currentChapter}
          currentPoem={currentPoem}
          onMenuClick={toggleMobileNav}
          isMenuOpen={mobileNavOpen}
        />

        {/* Mobile Drawer Overlay */}
        <div
          className={`mobile-nav-overlay ${mobileNavOpen ? 'visible' : ''}`}
          onClick={closeMobileNav}
          aria-hidden="true"
        />

        {/* Mobile Navigation Drawer */}
        <div className={`mobile-nav-drawer ${mobileNavOpen ? 'open' : ''}`}>
          {/* Drawer Handle */}
          <div className="drawer-handle" onClick={closeMobileNav}>
            <div className="drawer-handle-bar" />
          </div>

          {/* Current Location */}
          {chapter && (
            <div className="current-location">
              <div className="chapter-info">
                <span className="chapter-label">Chapter {currentChapter + 1} of 14</span>
                <h2 className="chapter-title">{chapter.title}</h2>
              </div>
              <div className="poem-info">
                <div className="poem-progress">
                  <span className="poem-label">Poem {currentPoem + 1} of {poems[currentChapter + 1]?.length || 9}</span>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${((currentPoem + 1) / (poems[currentChapter + 1]?.length || 9)) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Controls - More prominent on mobile */}
          <div className="mobile-nav-controls">
            <div className="mobile-control-row">
              <button
                className="mobile-nav-btn"
                onClick={() => { prevChapter(); closeMobileNav() }}
              >
                <span className="nav-icon">&#8593;</span>
                <span>Prev Chapter</span>
              </button>
              <button
                className="mobile-nav-btn"
                onClick={() => { nextChapter(); closeMobileNav() }}
              >
                <span className="nav-icon">&#8595;</span>
                <span>Next Chapter</span>
              </button>
            </div>
            <div className="mobile-control-row">
              <button
                className="mobile-nav-btn"
                onClick={() => { prevPoem(); closeMobileNav() }}
              >
                <span className="nav-icon">&#8592;</span>
                <span>Prev Poem</span>
              </button>
              <button
                className="mobile-nav-btn"
                onClick={() => { nextPoem(); closeMobileNav() }}
              >
                <span className="nav-icon">&#8594;</span>
                <span>Next Poem</span>
              </button>
            </div>
            <button
              className="mobile-nav-btn overview-btn"
              onClick={() => { resetToOverview(); closeMobileNav() }}
            >
              <span className="nav-icon">&#8962;</span>
              <span>Overview</span>
            </button>
          </div>

          {/* Chapter List */}
          <div className="chapter-list-section">
            <button
              className="section-toggle"
              onClick={toggleChapterList}
            >
              <span>All Chapters</span>
              <span className="toggle-icon">{showChapterList ? '\u2212' : '+'}</span>
            </button>

            {showChapterList && (
              <div className="chapter-list">
                {chapters.map((ch, index) => (
                  <button
                    key={ch.id}
                    className={`chapter-item ${currentChapter === index ? 'active' : ''}`}
                    onClick={() => handleChapterClick(index)}
                    style={{ '--chapter-color': ch.color }}
                  >
                    <span className="chapter-number">{ch.id}</span>
                    <span className="chapter-name">{ch.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    )
  }

  // Desktop layout (unchanged)
  return (
    <div className={`navigation ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Toggle Button */}
      <button
        className="nav-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? 'Collapse panel' : 'Expand panel'}
      >
        {isExpanded ? '\u2039' : '\u203A'}
      </button>

      {isExpanded && (
        <>
          {/* Header */}
          <header className="nav-header">
            <h1 className="nav-title">The Path(os)</h1>
            <p className="nav-subtitle">An Interactive Journey</p>
          </header>

          {/* Current Location */}
          {chapter && (
            <div className="current-location">
              <div className="chapter-info">
                <span className="chapter-label">Chapter {currentChapter + 1} of 14</span>
                <h2 className="chapter-title">{chapter.title}</h2>
              </div>
              <div className="poem-info">
                <div className="poem-progress">
                  <span className="poem-label">Poem {currentPoem + 1} of {poems[currentChapter + 1]?.length || 9}</span>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${((currentPoem + 1) / (poems[currentChapter + 1]?.length || 9)) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Chapter List */}
          <div className="chapter-list-section">
            <button
              className="section-toggle"
              onClick={toggleChapterList}
            >
              <span>All Chapters</span>
              <span className="toggle-icon">{showChapterList ? '\u2212' : '+'}</span>
            </button>

            {showChapterList && (
              <div className="chapter-list">
                {chapters.map((ch, index) => (
                  <button
                    key={ch.id}
                    className={`chapter-item ${currentChapter === index ? 'active' : ''}`}
                    onClick={() => handleChapterClick(index)}
                    style={{ '--chapter-color': ch.color }}
                  >
                    <span className="chapter-number">{ch.id}</span>
                    <span className="chapter-name">{ch.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Navigation Controls */}
          <div className="controls">
            <div className="control-group">
              <button className="control-btn" onClick={prevChapter} title="Previous Chapter">
                <span>&#8593;</span> Chapter
              </button>
              <button className="control-btn" onClick={nextChapter} title="Next Chapter">
                <span>&#8595;</span> Chapter
              </button>
            </div>

            <div className="control-group">
              <button className="control-btn" onClick={prevPoem} title="Previous Poem">
                <span>&#8592;</span> Poem
              </button>
              <button className="control-btn" onClick={nextPoem} title="Next Poem">
                <span>&#8594;</span> Poem
              </button>
            </div>

            <button className="control-btn overview" onClick={resetToOverview} title="Return to Overview (Esc)">
              Overview
            </button>
          </div>

          {/* Keyboard hints */}
          <div className="keyboard-hints">
            <p>Use arrow keys to navigate - ESC for overview</p>
          </div>
        </>
      )}
    </div>
  )
}

export default Navigation
